/**
 * Token ẩn danh cho rate-limit của backend (xem `ai-be/src/services/rate_limit/`).
 *
 * Backend bắt các API công khai (đăng nhập, lập lá số, ui-config, share…) mang
 * header `X-Anon-Token` khi chưa đăng nhập, thiếu là 401. Token do
 * `POST /v1/anon/token` phát, và endpoint đó đòi `X-Internal-Key` — một bí mật
 * CHỈ nằm ở server SSR. Vì vậy trình duyệt KHÔNG gọi thẳng backend mà xin qua
 * route cùng origin `POST /__boide/anon-token` (xử lý trong `src/server.ts`),
 * nơi server gắn key rồi forward.
 *
 * - Cache ở `localStorage` (backend giữ token 60 ngày, trượt theo lần dùng).
 * - Gộp các lượt xin đồng thời thành MỘT request (lượt tải trang đầu có cả chục
 *   API cùng bắn).
 * - Xin hỏng (mạng, 429 vì xin quá nhiều) ⇒ trả `null` và nghỉ một nhịp, KHÔNG
 *   ném: bên gọi cứ gửi request không có token, để backend tự quyết.
 */

export const ANON_TOKEN_ENDPOINT = "/__boide/anon-token";
const STORAGE_KEY = "boide_anon_token";
const FAILURE_COOLDOWN_MS = 15_000;

let memoryToken: string | null = null;
let inFlight: Promise<string | null> | null = null;
let blockedUntil = 0;

function readStored(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeStored(token: string | null) {
  try {
    if (token) window.localStorage.setItem(STORAGE_KEY, token);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // private mode / storage bị chặn — vẫn còn bản trong bộ nhớ
  }
}

async function requestNewToken(nativeFetch: typeof fetch): Promise<string | null> {
  if (Date.now() < blockedUntil) return null;
  try {
    const res = await nativeFetch(ANON_TOKEN_ENDPOINT, {
      method: "POST",
      credentials: "same-origin",
      cache: "no-store",
    });
    if (!res.ok) {
      const retryAfter = Number(res.headers.get("retry-after"));
      blockedUntil = Date.now() + (retryAfter > 0 ? retryAfter * 1000 : FAILURE_COOLDOWN_MS);
      return null;
    }
    const json = (await res.json()) as { token?: unknown };
    const token = typeof json.token === "string" && json.token ? json.token : null;
    if (token) {
      memoryToken = token;
      writeStored(token);
    }
    return token;
  } catch {
    blockedUntil = Date.now() + FAILURE_COOLDOWN_MS;
    return null;
  }
}

/** Token hiện có, hoặc xin mới nếu chưa có. `nativeFetch` là fetch GỐC (chưa bọc). */
export function getAnonToken(nativeFetch: typeof fetch): Promise<string | null> {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (!memoryToken) memoryToken = readStored();
  if (memoryToken) return Promise.resolve(memoryToken);
  if (!inFlight) {
    inFlight = requestNewToken(nativeFetch).finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}

/** Bỏ token đang giữ (backend báo không nhận ra nó — hết hạn, Redis bị dọn…). */
export function clearAnonToken(stale?: string | null) {
  // Nhiều request cùng nhận 401 với CÙNG token cũ: chỉ xoá nếu token đang giữ
  // vẫn là token đó, kẻo xoá mất token mới mà request đầu vừa xin về.
  if (stale && memoryToken && stale !== memoryToken) return;
  memoryToken = null;
  if (typeof window !== "undefined") writeStored(null);
}
